
import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  addDoc, 
  serverTimestamp 
} from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  User
} from "firebase/auth";
import { ExtractedData, TaskTemplate } from "../types";

const firebaseConfig = {
  apiKey: "AIzaSyCZs0OmnWcEBZswUnlD-4rUXwoFNHtU-6g",
  authDomain: "docuflow-automator.firebaseapp.com",
  projectId: "docuflow-automator",
  storageBucket: "docuflow-automator.firebasestorage.app",
  messagingSenderId: "477788768683",
  appId: "1:477788768683:web:3d66d55ec377f08a00eaee",
  measurementId: "G-K0K3T05P4D"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;

// --- Authentication Helpers ---

export const registerUser = async (email: string, pass: string, name?: string) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
  const user = userCredential.user;
  
  // Create user document in Firestore
  await setDoc(doc(db, "users", user.uid), {
    email: user.email,
    uid: user.uid,
    displayName: name || '',
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp()
  });
  
  return user;
};

export const loginUser = async (email: string, pass: string, rememberMe: boolean) => {
  // Set persistence based on "Remember Me"
  await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

  const userCredential = await signInWithEmailAndPassword(auth, email, pass);
  const user = userCredential.user;

  const userDocRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userDocRef);
  if (userSnap.exists()) {
    // Normal path: doc exists, only touch lastLogin (satisfies the update rule).
    await setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true });
  } else {
    // Edge case: account exists in Firebase Auth but has no Firestore doc
    // (e.g. created via Firebase console or before this app was deployed).
    // Write the full document so it passes the strict create rule.
    await setDoc(userDocRef, {
      email: user.email ?? '',
      uid: user.uid,
      displayName: user.displayName ?? '',
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    });
  }

  return user;
};

export const logoutUser = async () => {
  await firebaseSignOut(auth);
};

// Returns a fresh Firebase ID token for the current user, used to authenticate
// requests to our own backend (/api/*). Throws if no user is signed in.
export const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("You must be signed in to perform this action.");
  }
  return user.getIdToken();
};

export const resetUserPassword = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

// --- Firestore Data Management ---

const TEMPLATES_DOC_ID = "task_templates_v1";

export const saveTemplatesToCloud = async (templates: TaskTemplate[]) => {
  if (!auth.currentUser) throw new Error("User must be logged in to save templates.");
  
  const docRef = doc(db, "settings", TEMPLATES_DOC_ID);
  await setDoc(docRef, { 
    templates, 
    updatedAt: serverTimestamp(),
    lastUpdatedBy: auth.currentUser.uid 
  }, { merge: true });
};

export const loadTemplatesFromCloud = async (): Promise<TaskTemplate[] | null> => {
  if (!auth.currentUser) return null;

  try {
    const docRef = doc(db, "settings", TEMPLATES_DOC_ID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().templates as TaskTemplate[];
    }
  } catch (error: any) {
    console.error("Error loading templates:", error);
  }
  return null;
};

export const saveExtractionRecord = async (data: ExtractedData) => {
  if (!auth.currentUser) return; // Don't save if not logged in

  try {
    const recordsCol = collection(db, "extractions");
    await addDoc(recordsCol, {
      ...data,
      userId: auth.currentUser.uid,
      createdAt: serverTimestamp(),
      businessName: data.businessInfo.name,
      ownerName: data.ownerInfo.name
    });
  } catch (e) {
    console.warn("Failed to log extraction to Firebase:", e);
  }
};
